// The city briefing, as a live panel.
//
// A briefing is one of the two views of a city's page (the other is its guide,
// a document under /resources/). The markup is lib/place-city.ts and the
// behaviour is place/city-page.ts — both shared with the guide — so what is
// left here is what only the app knows: when to fetch a series, where on the
// map the city is, and how the page's "selected section" and "selected rating"
// map onto the app's own state. Picking a cell in the calendar scrubs the map
// and repaints its field, because in the app those are the same thing.
//
// Everything this needs from the rest of the app arrives as `deps` rather than
// as imports. Opening a city moves the map, names a camera, writes the URL and
// scrubs the timeline — all decisions that belong to the app, not to the panel
// that displays a series.

import { $ } from '../dom.ts';
import { map } from '../map/instance.ts';
import { state, store } from '../state.ts';
import { setSelectedHighlight } from '../map/layers.ts';
import { track } from '../analytics.ts';
import { evalRecipe } from '../../lib/recipe.ts';
import type { Recipe } from '../../lib/recipe.ts';
import { VAR } from '../../lib/variables.ts';
import type { VarKey } from '../../lib/variables.ts';
import { countrySlug } from '../../lib/slug.ts';
import { yearsPhrase } from '../../lib/year-span.ts';
import { cityMastHtml, ownGuide } from '../../lib/place-city.ts';
import type { CityPageState } from '../../lib/place-city.ts';
import { crumbsHtml, briefingCrumbs, BACK_TO_MAP } from '../../lib/place-view.ts';
import { initCityPage, paintCityPage, refreshCityPage, rebuildCalendar } from '../place/city-page.ts';
import { rememberPlace } from '../last-place.ts';
import { positionRing } from '../ui/tooltip.ts';
import type { CitySeries, CityHint } from '../../contracts/city.ts';

// What the panel needs the app to do on its behalf.
export interface CityPanelDeps {
  // Switch the whole screen to the city view.
  setView: (v: 'landing' | 'map' | 'city') => void;
  // Name the reader's camera, so the URL stops being a country path.
  markUserCamera: () => void;
  // Remember the state, and mirror it into the address bar.
  persist: () => void;
  // The activities as the reader currently sees them — the curated five, plus
  // the custom score once unlocked. One source, so calendar rows and the lens
  // menu cannot disagree about which row is which.
  activityKeys: () => VarKey[];
  // Scrub the whole app to a section.
  showFrame: (idx: number) => void;
  // Stop playback (a deliberate scrub takes over from it).
  pause: () => void;
  togglePlay: () => void;
  // Make a rating the one the map is painting. Picking a row in the calendar is
  // choosing a lens, so it goes through the app's own mutator — everything
  // downstream (the field, the sentence, the URL) is a subscriber to that.
  applyVariable: (v: VarKey) => void;
  setBucket: (key: string) => void;
  setRecipe: (r: Recipe, opts?: { defer?: boolean }) => void;
  unlockCustom: () => void;
  // The map, at full size.
  goMap: () => void;
}

let deps: CityPanelDeps;

// The time of day, as the bar's third word ("Midday" -> "midday").
function bucketWord(): string {
  const bk = state.manifest?.buckets.find((b) => b.key === state.bucket);
  return (bk?.label || state.bucket).toLowerCase();
}

// The rating the page is showing: the map's lens, when it is a rating. A plain
// measurement is not a judgement, so then there is none.
const ratingNow = (): VarKey | null => (VAR[state.variable]?.score ? state.variable : null);

// The page's state, read off the app's.
function pageState(): CityPageState {
  const s = state.series;
  const years = state.manifest?.years || [];
  const own = s ? ownGuide(s) : null;
  const sel = state.selectedCity;
  const slug = sel && sel.citySlug && s ? `/${countrySlug(s.country)}/${sel.citySlug}/` : location.pathname;
  return {
    view: 'briefing',
    dekad: state.dateIdx,
    rating: ratingNow(),
    // The reader's own rating is the calendar's last row, under the curated five.
    acts: [...deps.activityKeys()].sort((a, b) => Number(!!VAR[a].custom) - Number(!!VAR[b].custom)),
    recipe: state.recipe,
    years,
    bucket: bucketWord(),
    guideHref: own ? own.href : null,
    briefingHref: slug,
    span: yearsPhrase(years),
    mapLink: BACK_TO_MAP,
  };
}

export function initCityPanel(d: CityPanelDeps): void {
  deps = d;
  initCityPage({
    scope: $('cityview'),
    slots: { mast: $('citymast'), answer: $('cityheader'), body: $('citybody') },
    series: () => (state.series && isOpenCity(state.series) ? state.series : null),
    dates: () => state.dates,
    pageState,
    // A cell names a RATING and a TIME, and picking it means both — so the map
    // paints that rating at that section. The time first: everything that
    // follows a new rating reads the date it is at.
    pick(i, act) {
      deps.pause();
      deps.showFrame(i);
      if (act) deps.applyVariable(act);
      deps.persist();
      refreshCityPage();
    },
    setRating(act) {
      deps.applyVariable(act);
      deps.persist();
      refreshCityPage();
    },
    setRecipe: (r, o) => deps.setRecipe(r, o),
    unlockCustom: () => deps.unlockCustom(),
    openMap: () => deps.goMap(),
    togglePlay() {
      deps.togglePlay();
      refreshCityPage();
    },
    playing: () => state.playing,
    buckets: () => (state.manifest?.buckets || []).map((b) => ({
      key: b.key,
      label: b.label,
      hint: `${String(b.targetHour).padStart(2, '0')}:00 local`,
      on: b.key === state.bucket,
    })),
    setBucket: (k) => deps.setBucket(k),
    narrow: () => window.innerWidth <= 760,
  });
}

// The city whose briefing is already in the document, baked by the pre-render —
// read once, because it is only ever true of the very first city this page
// opens. Everything after that is a navigation, and navigations build their own.
const prerendered = prerenderedCityId();

function prerenderedCityId(): number | null {
  if (typeof document === 'undefined') return null;
  const raw = document.body && document.body.dataset ? document.body.dataset.prerenderCity : null;
  const id = raw == null ? NaN : Number(raw);
  return Number.isInteger(id) ? id : null;
}

// True while the baked briefing on screen is this city's and has not yet been
// replaced. Cleared by the first render, so a second visit to the same city
// (Back, or a dot click) renders normally.
let adopting = prerendered != null;

/**
 * Open a city's briefing.
 *
 * `animate` is false on the boot path: a reader who typed a city URL should
 * arrive at that city, not watch the globe fly to it — the same reason
 * applyRouteFromUrl jumps rather than flies for a camera or a country. Every
 * other way in (a dot click, the search box, a Back) keeps the flight, which
 * there is a transition between two places the reader can see.
 */
export async function openCity(id: number, hint?: CityHint | null, { animate = true }: { animate?: boolean } = {}) {
  const token = ++state.seriesToken;
  // Every way into a city funnels through here — a dot, a label, the search, a
  // peaks row, a recipe — so this is the one place that counts them. Which city
  // is content, not a fact about the reader; nothing else is recorded.
  track('city_open', { city: id, country: (hint && hint.country) || '' });
  // Opening a city leaves any country framing behind, so a later maximise names
  // the reader's own camera rather than resurrecting the old /{country}/ path.
  deps.markUserCamera();
  state.selectedCity = { id, ...(hint || {}) };
  deps.setView('city');
  setSelectedHighlight(id); // keep the open city's dot ringed green
  // The briefing already on screen is this city's only if the pre-render baked it.
  const adopt = adopting && prerendered === id;
  adopting = false;
  // Any other city is a transition the reader can see, so the briefing rises into
  // view again — `prerendered` only ever describes the page as it was served.
  if (!adopt) document.body.classList.remove('prerendered');
  if (hint && hint.lon != null) {
    // Re-measure to the band FIRST (map.resize forces the reflow synchronously), so
    // the move frames the city in the band rather than at the old full-viewport
    // centre — where the briefing body would cover it.
    map.resize();
    moveToCity(hint.lon, hint.lat!, animate);
  }
  // The masthead shows what we already know; the body shows a loading line until
  // the series lands. Neither is written over a baked briefing: it already says
  // both, in full, and replacing it with "Loading…" would undo the point of it.
  if (!adopt) {
    renderShell(hint);
    $('cityheader').innerHTML = '';
    $('citybody').innerHTML = '<div class="extras-desc city-wait">Loading the typical-year briefing…</div>';
  }
  try {
    const res = await fetch(`/city/${id}/series?bucket=${state.bucket}`);
    const data: CitySeries | null = res.ok ? await res.json() : null;
    if (token !== state.seriesToken) return;
    if (!data) throw new Error('no data');
    computeCustomSeries(data);
    // Through the store, not a bare write: subscribers size themselves from this
    // city's ratings and have to be told a new one arrived. The store compares
    // by identity, and each fetch is a fresh object, so this always fires.
    store.set({ series: data });
    if (!hint || hint.lon == null) {
      moveToCity(data.lon, data.lat, animate);
    }
    renderCity({ adopt });
    positionRing(); // the pulse marks the open city once it is known where it is
    // The landing's sentence opens on the last place looked at (last-place.ts).
    rememberPlace(data, state.dates, { custom: state.customOn, bucket: state.bucket });
    // Once the map box has finished moving into the band (420ms, ui/layout.ts).
    setTimeout(reframeCity, 520);
  } catch {
    if (token === state.seriesToken) {
      $('citybody').innerHTML = '<div class="extras-desc city-wait">Couldn’t load this city’s data.</div>';
    }
  }
  deps.persist();
}

// Frame the city in the band. The masthead is set over the band's left on a
// wide screen and over its lower half on a phone, so the camera is padded to
// put the place in what is left: right of the name, or above it. Zoom only ever
// increases — a reader already zoomed in past 4 keeps their scale. `animate`
// false lands there with no flight at all — the boot path, where there is no
// previous view for a flight to come from.
//
// The padding is a property of the BAND, measured when it is applied — and
// MapLibre keeps it on the camera afterwards. So it is re-applied whenever the
// band changes size (reframeCity), and taken off again when the briefing closes
// (clearCityFrame): a padding measured for one box and left on another is how
// the open city ended up at the band's edge, or off it.
let framed: { lon: number; lat: number } | null = null;
// The reader has dragged the band's map: where it sits is theirs now.
let readerMoved = false;

function bandPadding() {
  const el = map.getContainer();
  // The band's CSS size, not the container's: mid-transition the container is
  // pinned somewhere between the full map and the band (ui/layout.ts).
  const band = $('city-band');
  const W = band.clientWidth || el.clientWidth, H = band.clientHeight || el.clientHeight;
  return W < 760
    ? { left: 30, right: 30, top: 70, bottom: Math.round(H * 0.45) }
    : { left: Math.round(W * 0.42), right: 60, top: 80, bottom: Math.min(60, Math.round(H * 0.15)) };
}

let dragWired = false;
function moveToCity(lon: number, lat: number, animate: boolean) {
  // Wired on first use: the map does not exist yet when this module is set up.
  if (!dragWired) {
    dragWired = true;
    map.on('dragstart', () => { if (state.view === 'city') readerMoved = true; });
  }
  framed = { lon, lat };
  readerMoved = false;
  const to = { center: [lon, lat] as [number, number], zoom: Math.max(map.getZoom(), 4), padding: bandPadding() };
  if (animate) map.flyTo({ ...to, duration: 700 });
  else map.jumpTo(to);
}

// The band changed size (the window did, or the map box finished moving into
// it): put the open city back where the masthead leaves room for it.
export function reframeCity() {
  if (state.view !== 'city' || !framed || readerMoved) return;
  map.resize();
  map.jumpTo({ center: [framed.lon, framed.lat], padding: bandPadding() });
  positionRing();
}

// Leaving the briefing: the full map is framed by nothing.
export function clearCityFrame() {
  framed = null;
  map.setPadding({ top: 0, right: 0, bottom: 0, left: 0 });
}

// Refetch the current city's series for the active bucket, without moving the map.
export async function reloadSeries() {
  const sel = state.selectedCity;
  if (!sel) return;
  const token = ++state.seriesToken;
  try {
    const res = await fetch(`/city/${sel.id}/series?bucket=${state.bucket}`);
    const data: CitySeries | null = res.ok ? await res.json() : null;
    if (token !== state.seriesToken || !data) return;
    computeCustomSeries(data);
    store.set({ series: data }); // see openCity
    renderCity();
  } catch {
    /* keep the last render */
  }
}

// Fill in the "My kind of day" row of a city's series from the component arrays the
// server already sent — the same client-side scoring the dots use, one slot per
// section. Server untouched: /city/:id/series has never heard of recipes.
export function computeCustomSeries(s: CitySeries) {
  const sv = s.series || {};
  const at = (vk: string, i: number) => (sv[vk] ? sv[vk][i] : null);
  sv.custom = (s.dates || []).map((_, i) => {
    const sc = evalRecipe({ app: at('app', i), ws: at('ws', i), rain: at('rain', i), sky: at('sky', i), v: at('v', i) }, state.recipe);
    return sc == null ? null : Math.round(sc);
  });
  // Invalidate any smoothed cache of a previous recipe's row.
  if (s._sm) delete s._sm.custom;
}

// What can be drawn from a dot click alone, before the series lands: the name,
// the place under it, and where the page sits.
function renderShell(hint?: CityHint | null) {
  const name = (hint && hint.name) || 'City';
  $('crumbs').innerHTML = crumbsHtml(briefingCrumbs(hint?.country));
  $('citymast').innerHTML = cityMastHtml(
    { name, country: (hint && hint.country) || '', admin: hint?.admin, pop: hint?.pop || 0 },
    { view: 'briefing', guideHref: null, briefingHref: null },
  );
}

// Whether a series is the OPEN city's. `state.series` is not always: a dot click
// (or a search hit, or a Back) names the new city in state.selectedCity at once
// and its series lands a fetch later, so anything that repaints in between —
// changing the lens, crossing a breakpoint — has the PREVIOUS city's series in
// hand. openCity guards its own render with state.seriesToken; this is the guard
// for every other caller, and the page controller reads the series through it.
function isOpenCity(s: CitySeries): boolean {
  const sel = state.selectedCity;
  return !sel || s.id === sel.id;
}

// Paint the whole briefing. With `adopt`, it is already in the document — baked
// by the pre-render — so the markup is left exactly where it is and only the
// parts a static file could not know are filled in: the verdict, the selected
// section's readings, and the chart's marker. That is the difference between a
// page that is already there and one that rebuilds itself on arrival.
export function renderCity({ adopt = false }: { adopt?: boolean } = {}) {
  const s = state.series;
  if (!s || !isOpenCity(s)) return;
  $('crumbs').innerHTML = crumbsHtml(briefingCrumbs(s.country));
  if (adopt) {
    // A reader who has made the custom rating their own gets its row; the baked
    // page could only offer it.
    if (state.customOn) rebuildCalendar();
    refreshCityPage();
  } else {
    paintCityPage();
  }
}

// What follows the selected section, the rating and the recipe. The app calls
// these from its store subscriptions; each is the page catching up with state
// the app already changed.
export function updateCityCharts() {
  if (state.view !== 'city' || !state.series) return;
  refreshCityPage();
}

// The rating list or the recipe behind the custom row changed.
export function buildCalendar(_s?: CitySeries) {
  if (state.view !== 'city' || !state.series) return;
  rebuildCalendar();
  refreshCityPage();
  // The reader's rating changed, and so may when this place is best for it.
  if (isOpenCity(state.series)) rememberPlace(state.series, state.dates, { custom: state.customOn, bucket: state.bucket });
}
